import React, { useCallback, useEffect, useMemo, useState } from 'react';
import OrdererInfo from '../../../components/purchase/ordererinfo';
import TextWinds from '../../../components/common/TextWinds';
import NormalPage from '../../../components/layout/NormalPage';
import FullWidthPage from '../../../components/layout/FullWidthPage';
import DeliveryInfo from '../../../components/purchase/DeliveryInfo';
import OrderProductInfo from '../../../components/purchase/orderproductinfo';
import MemberShipInfo from '../../../components/purchase/membershipinfo';
import AmountPayment from '../../../components/purchase/amountpayment';
import PaymentMethod from '../../../components/purchase/PaymentMethod';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import {
  getPurchaseInfo,
  resetPurchaseInfo,
  selectPurchaseInfo,
} from '../../../store/features/purchaseSlice';
import { isAxiosError } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getDiscountAmount,
  getOrderItemTotalAmount,
} from '../../../api/utils/order-item-total-amount';
import {
  Device,
  PaymentMethod as PaymentMethodEnum,
  PaymentType,
  ProductInstallmentType,
  ProductType,
} from '../../../enum';
import { purchase_valid } from '../../../api/utils/purchase-validation';
import TossPayments from '../../../components/purchase/tossPayments';
import { selectDeliveryInfo } from '../../../store/features/orderInfoSlice';
import { OrderItem } from '../../../constants/types';
import { DISPLAY_PRICE_OVERRIDES } from '../../../constants/constants';
import { openAlert } from '../../../store/features/modalSlice';
import { LoadingSpinner } from '../../../components/Loading/LoadingSpinner';
import TermsAgreement from '../../../components/purchase/TermsAgreement';
import { PurchaseRequestDto } from '../../../constants/types/purchase-type';
import { purchaseRequest } from '../../../api/utils/api/purchaseApi';
import {
  isProductPurchasable,
  PRODUCT_INACTIVE_MESSAGE,
} from '../../../utils/product-active';

/** 모바일 환경인지 판별 */
const isMobileDevice = () => {
  const mobileDevices =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/;
  return mobileDevices.test(navigator.userAgent.toLowerCase());
};

/**
 * 결제 요청 파라미터 조립
 * - 배송 정보, 주문 정보는 Redux store에서, 나머지는 컴포넌트 상태에서 수집
 */
const buildPurchaseParam = ({
  deliveryInfo,
  message,
  pointAmount,
  paymentMethod,
  templateId,
  purchaseInfo,
}: {
  deliveryInfo: ReturnType<typeof selectDeliveryInfo>;
  message: string;
  pointAmount: number;
  paymentMethod: string;
  templateId: string;
  purchaseInfo: ReturnType<typeof selectPurchaseInfo>;
}): PurchaseRequestDto => ({
  device: isMobileDevice() ? Device.MOBILE : Device.PC,
  address_1: deliveryInfo?.address,
  address_2: deliveryInfo?.detailAddress,
  message,
  point_used: pointAmount,
  contact_no: deliveryInfo?.phone,
  payment_method: paymentMethod as PaymentMethodEnum,
  post_code: deliveryInfo?.zipCode,
  receiver: deliveryInfo?.name,
  template_id: templateId,
  installment_type:
    purchaseInfo?.order_items.length === 1
      ? purchaseInfo.order_items[0].product.installment_type
      : ProductInstallmentType.STANDARD,
});

/**
 * 결제 페이지
 */
const Purchase = () => {
  const navigate = useNavigate();
  const templateId = (useLocation().state as { purchaseId?: string })
    ?.purchaseId;

  const dispatch = useAppDispatch();

  /** 주문 정보 */
  const purchaseInfo = useAppSelector(selectPurchaseInfo);
  /** 배송 정보 */
  const deliveryInfo = useAppSelector(selectDeliveryInfo);
  /** 포인트 사용 금액 */
  const [pointAmount, setPointAmount] = useState(0);
  /** 결제 수단 */
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodEnum | ''>(
    '',
  );
  /** 배송 메시지 */
  const [message, setMessage] = useState('안전하게 배송해주세요!');
  /** 모든 필수 약관에 대해서 모두 동의 되었는지 여부 */
  const [hasAllRequiredChecked, setHasAllRequiredChecked] = useState(false);
  /** 결제 진행 중 로딩 표시 */
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  /** 전액포인트 결제 여부 */
  const [isOnlyPointPayment, setIsOnlyPointPayment] = useState(false);

  /** 초기 데이터 로드 */
  useEffect(() => {
    /** 템플릿 ID가 없으면 메인 페이지로 리다이렉트 */
    if (!templateId) {
      navigate('/', { replace: true });
      return;
    }
    /** 같은 주문으로 재진입(뒤로가기 등)이면 기존 상태 유지, fetch 생략 */
    if (templateId === purchaseInfo?.id) {
      return;
    }
    dispatch(resetPurchaseInfo());
    dispatch(getPurchaseInfo({ url: '/v1/order/template/' + templateId }));
  }, [templateId]);

  /** 금액 계산: purchaseInfo에서 파생되는 값이므로 state 대신 useMemo 사용 */
  const { totalProductAmount, totalDeliveryAmount, totalDiscountAmount } =
    useMemo(() => {
      if (!purchaseInfo)
        return {
          totalProductAmount: 0,
          totalDeliveryAmount: 0,
          totalDiscountAmount: 0,
        };

      const { productAmount, discountAmount } = purchaseInfo.order_items.reduce(
        (acc, item: OrderItem) => ({
          productAmount: acc.productAmount + getOrderItemTotalAmount(item),
          discountAmount:
            acc.discountAmount + getDiscountAmount('purchase', item),
        }),
        { productAmount: 0, discountAmount: 0 },
      );

      return {
        totalProductAmount: productAmount,
        totalDeliveryAmount: purchaseInfo.shipping_fee ?? 0,
        totalDiscountAmount: discountAmount,
      };
    }, [purchaseInfo]);

  // FIXME: 기간한정 상품 오류로 가격 하드코딩 — 결제 금액 표기용 (실제 결제 금액은 서버 가격)
  // 대상 상품은 상품금액을 하드코딩 가격으로, 할인금액을 0으로 대체
  const { displayProductAmount, displayDiscountAmount } = (
    purchaseInfo?.order_items ?? []
  ).reduce(
    (acc, item: OrderItem) => {
      const price = DISPLAY_PRICE_OVERRIDES[item.product.id];
      if (price === undefined) return acc;
      return {
        displayProductAmount:
          acc.displayProductAmount - getOrderItemTotalAmount(item) + price,
        displayDiscountAmount:
          acc.displayDiscountAmount - getDiscountAmount('purchase', item),
      };
    },
    {
      displayProductAmount: totalProductAmount,
      displayDiscountAmount: totalDiscountAmount,
    },
  );

  /** 전액포인트 결제 시 결제수단 선택 없어도 됨 */
  useEffect(() => {
    if (
      pointAmount > 0 &&
      pointAmount ===
        totalProductAmount + totalDeliveryAmount - totalDiscountAmount
    ) {
      setPaymentMethod(PaymentMethodEnum.POINT);
      setIsOnlyPointPayment(true);
    } else {
      /** 고객이 전액포인트였다가 일반결제로 다시 전환했을 떄 paymentMethod가 POINT로 남아있으면 초기화 */
      if (paymentMethod === PaymentMethodEnum.POINT) {
        setPaymentMethod('');
      }
      setIsOnlyPointPayment(false);
    }
  }, [
    pointAmount,
    totalProductAmount,
    totalDeliveryAmount,
    totalDiscountAmount,
  ]);

  /** 결제 요청 파라미터 — isValid 검사와 handlePurchase 양쪽에서 공유 */
  const purchaseParam = useMemo(
    () =>
      buildPurchaseParam({
        deliveryInfo,
        message,
        pointAmount,
        paymentMethod,
        templateId,
        purchaseInfo,
      }),
    [
      deliveryInfo,
      message,
      pointAmount,
      paymentMethod,
      templateId,
      purchaseInfo,
    ],
  );

  /** 주문 상품 중 판매 중지(is_active === false) 상품이 하나라도 있으면 결제 불가 */
  const hasInactiveProduct = useMemo(
    () =>
      purchaseInfo?.order_items.some(
        (item: OrderItem) => !isProductPurchasable(item.product),
      ) ?? false,
    [purchaseInfo],
  );

  /** 결제하기 버튼 활성화 여부 결정: 현재 입력 상태 기준으로 결제 유효성 실시간 검사 (약관 전체 동의 포함) */
  const isValid = useMemo(() => {
    /* 전액포인트 결제 시 결제수단:포인트, 전액포인트 결제 아닐때 결제수단:카드, 계좌이체, 가상계좌*/
    const isSelectedPaymentMethod = isOnlyPointPayment
      ? paymentMethod === PaymentMethodEnum.POINT
      : paymentMethod === PaymentMethodEnum.CREDIT_CARD ||
        paymentMethod === PaymentMethodEnum.BANK_TRANSFER ||
        paymentMethod === PaymentMethodEnum.DEPOSIT;

    return (
      !hasInactiveProduct &&
      isSelectedPaymentMethod &&
      hasAllRequiredChecked &&
      purchase_valid(purchaseParam).flag
    );
  }, [
    hasAllRequiredChecked,
    purchaseParam,
    paymentMethod,
    isOnlyPointPayment,
    hasInactiveProduct,
  ]);

  /**
   * 포인트 사용 가능 여부: 주문 항목 '전체'가 아래 3가지를 모두 만족해야 true
   * 1. 코스 상품(ProductType.COURSE)이면 연장 상품(is_extension_product)일 것
   *    - 코스상품 중 연장상품 → 포인트 사용 가능 / 코스상품 중 연장이 아닌 일반코스상품 → 포인트 사용 불가 / 코스가 아닌 상품 → 이 조건과 무관함
   * 2. 포인트쿠폰 상품(is_point_product)이 아닐 것
   * 3. 상품별 포인트 사용 가능 여부(can_use_point)가 true일 것
   */
  const isPointAvailable = useMemo(
    () =>
      purchaseInfo?.order_items.every(
        (item: OrderItem) =>
          (item.product.is_extension_product === true ||
            item.product.product_type !== ProductType.COURSE) &&
          item.product.is_point_product !== true &&
          item.product.can_use_point === true,
      ),
    [purchaseInfo],
  );

  /**
   * 결제하기 핸들러
   * @description 결제 파라미터 조립 → 유효성 검사 → PG사 결제 요청 순으로 진행
   */
  const handlePurchase = useCallback(async () => {
    /** 판매 중지 상품이 포함된 주문은 결제 차단 */
    if (hasInactiveProduct) {
      dispatch(openAlert({ message: PRODUCT_INACTIVE_MESSAGE }));
      return;
    }

    /** 결제 진행 중 로딩 표시 */
    setIsPaymentProcessing(true);

    /** PG사 결제 요청 */
    try {
      /** 결제 요청 */
      const data = await purchaseRequest(purchaseParam);

      switch (data.paymentType) {
        case PaymentType.TOSS:
          /** 토스페이먼츠 결제창 띄우기 */
          await TossPayments(data.paymentConfig);
          break;
        case PaymentType.POINT_ONLY:
        case PaymentType.FREE:
          /**성공, 실패 경우 모두 리다이렉트 */
          const { pathname, search } = new URL(data.redirectUrl);
          navigate(pathname + search);
          break;
        default:
          throw new Error(`unknown paymentType: 알 수 없는 결제 유형입니다.`);
      }
      /** 에러처리
       * case1. purchaseRequest 에러
       * case2. toss SDK 결제창 에러
       * @see https://docs.tosspayments.com/sdk/v2/error-codes#결제창-에러
       * INFO: toss 결제창 에러 중 USER_CANCEL를 catch문에서 잡는것을 보면 결제창 오류 모두 이쪽에서 캐치하는것으로 보임.
       */
    } catch (error: unknown) {
      /** TOSS 결제창 에러 중 사용자가 결제창을 닫은 경우 (USER_CANCEL) — 에러 얼럿 없이 종료
       *   console.log(JSON.stringify(error)); // {"code":"USER_CANCEL"}
       */
      if ((error as { code?: string })?.code === 'USER_CANCEL') return;

      /** 인터셉터에서 이미 알럿을 띄운 경우 중복 방지 */
      if ((error as { __handled?: boolean })?.__handled) return;

      /** 결제 도중 에러 시 에러 얼럿 - 서버 에러 응답이 있으면 서버 메시지 우선, 없으면 고정 문구로 폴백 */
      const message = isAxiosError(error)
        ? (error.response?.data?.message ??
          '결제 중 오류가 발생했습니다. 다시 시도해주세요.')
        : error instanceof Error
          ? error.message
          : '결제 중 오류가 발생했습니다. 다시 시도해주세요.';
      dispatch(openAlert({ message }));
    } finally {
      /** 결제 진행 중 로딩 표시 해제 */
      setIsPaymentProcessing(false);
    }
  }, [purchaseParam, dispatch, hasInactiveProduct]);

  return (
    templateId && (
      <NormalPage bgColor="white" xlBgColor="gray0" paddingX={0}>
        <FullWidthPage bgColor="transparent">
          <section className="hidden pb-[24px] pt-[32px] xl:flex">
            <TextWinds type="title_h1">결제</TextWinds>
          </section>

          <section className="flex flex-col gap-6 pt-6 xl:flex-row">
            {/* 결제 페이지 메인 패널 */}
            <div className="flex w-full flex-row justify-center xl:basis-3/4">
              {purchaseInfo && (
                <div className="relative h-full w-full">
                  {/* 주문자 정보 */}
                  <OrdererInfo />

                  {/* 배송지 정보 및 배송 메세지 */}
                  <DeliveryInfo
                    deliveryMessage={message}
                    onDeliveryMessageChange={setMessage}
                  />

                  {/* 주문 상품 정보 */}
                  <OrderProductInfo purchaseInfo={purchaseInfo} />

                  {/* 포인트 사용 — 코스/포인트 상품 제외 시에만 노출 */}
                  {isPointAvailable && (
                    <MemberShipInfo
                      originPrice={
                        totalProductAmount +
                        totalDeliveryAmount -
                        totalDiscountAmount
                      }
                      setPointAmount={setPointAmount}
                    />
                  )}

                  {/* 결제 수단 선택 — 전액포인트 결제 시 노출 안함 */}
                  {!isOnlyPointPayment && (
                    <PaymentMethod
                      paymentMethod={paymentMethod}
                      onPaymentMethodChange={setPaymentMethod}
                    />
                  )}

                  {/* 약관 동의 */}
                  <TermsAgreement
                    termsList={purchaseInfo.terms}
                    onAllRequiredChecked={setHasAllRequiredChecked}
                  />
                </div>
              )}
            </div>

            {/* 결제 페이지 서브 패널 (오른쪽) */}
            <div className="t-0 w-full xl:basis-1/4">
              {/* 결제 금액 요약 및 결제하기 버튼 */}
              <AmountPayment
                productAmount={displayProductAmount}
                deliveryAmount={totalDeliveryAmount}
                discountAmount={displayDiscountAmount}
                pointAmount={pointAmount}
                handlePurchase={handlePurchase}
                isValid={isValid}
              />
            </div>
          </section>

          {/* 결제 진행 중 로딩 오버레이 */}
          {isPaymentProcessing && (
            <div className="fixed left-0 top-0 flex h-full w-full justify-center bg-gray2 opacity-80">
              <div className="mx-auto flex w-fit flex-col items-center justify-center gap-5 py-10">
                <LoadingSpinner />
                <div>결제 진행중입니다</div>
              </div>
            </div>
          )}
        </FullWidthPage>
      </NormalPage>
    )
  );
};

export default Purchase;
